(() => {
  'use strict';
  const tagName = 'coral-mobile-collection-tools';
  if (customElements.get(tagName)) return;

  class CoralMobileCollectionTools extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      this.media = window.matchMedia('(max-width: 749px)');
      this.activeDialog = null;
      this.pendingSource = null;
      this.pendingCommit = null;
      this.priceLoans = [];
      this.nativeRequests = new Map();
      this.originSection = this.closest('.shopify-section');

      // Only one instance per collection section, including Theme Editor reloads.
      document.querySelectorAll(tagName).forEach((element) => {
        if (element !== this && element.dataset.sectionId === this.dataset.sectionId) element.remove();
      });

      // A transformed page ancestor would otherwise capture position:fixed.
      this.moving = true;
      document.body.appendChild(this);
      this.moving = false;

      const options = { signal: this.events.signal };
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('input', (event) => this.onInput(event), options);
      this.addEventListener('submit', (event) => this.onSubmit(event), options);
      this.addEventListener('coral:price-draft-change', () => this.updateGroupDots(), options);
      this.addEventListener('keydown', (event) => this.onKeyDown(event), options);

      // Route the visible mobile collection toolbar into these sticky sheets.
      // Capture phase is intentional: it prevents the theme's native Filter drawer
      // and coral-facet-sort drawer from opening first.
      document.addEventListener(
        'click',
        (event) => this.onNativeToolbarTrigger(event),
        { ...options, capture: true }
      );

      this.querySelectorAll('[data-cs-dialog]').forEach((dialog) => {
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        }, options);
        dialog.addEventListener('close', () => {
          if (this.activeDialog === dialog && !dialog.open) this.close();
        }, options);
        dialog.addEventListener('pointerdown', (event) => {
          this.startedOutside = this.isOutsideDialog(event, dialog);
        }, options);
        dialog.addEventListener('click', (event) => {
          if (this.startedOutside && this.isOutsideDialog(event, dialog)) this.close();
          this.startedOutside = false;
        }, options);
      });
      this.media.addEventListener('change', () => this.updateViewport(), options);
      document.addEventListener('coral:facets-rendered', (event) => this.onFacetsRendered(event), options);
      document.addEventListener('input', (event) => this.onNativeInput(event), { ...options, capture: true });
      document.addEventListener('submit', (event) => {
        // Borrowed price inputs deliberately retain native form ownership. Route
        // implicit/keyboard submits to Apply instead of navigating the native form.
        if (this.priceLoans.length && event.target === this.nativeContext()?.form) {
          event.preventDefault();
          this.querySelector('[data-cs-filter-form]')?.requestSubmit();
        }
      }, { ...options, capture: true });
      document.addEventListener('coral:facets-start', (event) => this.onNativeStart(event), options);
      document.addEventListener('coral:facets-complete', (event) => this.onNativeComplete(event), options);
      document.addEventListener('coral:facets-error', (event) => this.onNativeError(event), options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId) this.remove();
      }, options);
      window.addEventListener('pageshow', () => {
        this.close();
        // A bfcache restore can retain disabled UI from an interrupted request.
        this.pendingCommit = null;
        this.nativeRequests.clear();
        this.nativeInputPending = false;
        clearTimeout(this.nativeInputTimer);
        this.setBusy(false);
        this.syncSortSelects(this.dataset.currentSort);
      }, options);
      // Observe, never modify, the original mobile drawer. Only our new bar
      // yields while it is open, keeping its existing Apply/Clear controls usable.
      this.nativeDrawer = this.originSection?.querySelector('#main-collection-filters .mobile-facets__disclosure')
        || document.querySelector('#main-collection-filters .mobile-facets__disclosure');
      if (this.nativeDrawer) {
        this.nativeDrawerObserver = new MutationObserver(() => this.updateNativeDrawerState());
        this.nativeDrawerObserver.observe(this.nativeDrawer, { attributes: true, attributeFilter: ['open'] });
      }
      this.updateNativeDrawerState();
      // The original Filter & Sort remains available with or without this script.
      this.removeAttribute('hidden');
      this.dataset.ready = '';
      this.updateViewport();
      this.updateGroupDots();
    }

    disconnectedCallback() {
      if (this.moving) return;
      this.close(false);
      this.events?.abort();
      this.nativeDrawerObserver?.disconnect();
      clearTimeout(this.slowTimer);
      clearTimeout(this.nativeInputTimer);
      this.initialized = false;
    }

    updateViewport() {
      if (!this.media.matches) this.close(false);
    }

    updateNativeDrawerState() {
      const isOpen = Boolean(this.nativeDrawer?.open);
      this.toggleAttribute('data-native-drawer-open', isOpen);
      if (isOpen && this.activeDialog) this.close(false);
    }

    /*
     * Intercept the upper mobile Filter / Sort controls and open the existing
     * Coral sticky sheets instead of Shopify's default mobile drawers.
     */
    onNativeToolbarTrigger(event) {
      if (!this.media.matches) return;
      if (event.defaultPrevented) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const filterTrigger = target.closest(
        '#main-collection-filters .mobile-facets__open-wrapper'
      );

      if (
        filterTrigger &&
        this.originSection?.contains(filterTrigger) &&
        this.querySelector('[data-cs-dialog="filter"]')
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.open('filter', filterTrigger);
        return;
      }

      const sortTrigger = target.closest('[data-coral-sort-trigger]');

      if (
        sortTrigger &&
        this.originSection?.contains(sortTrigger) &&
        this.querySelector('[data-cs-dialog="sort"]')
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.open('sort', sortTrigger);
      }
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-cs-open')) this.open(button.dataset.csOpen, button);
      else if (button.hasAttribute('data-cs-close')) this.close();
      else if (button.hasAttribute('data-cs-tab')) this.selectTab(button);
      else if (button.hasAttribute('data-cs-clear')) this.clearDraft();
    }

    onInput(event) {
      if (!event.target.closest('[data-cs-filter-form]')) return;
      this.updateGroupDots();
    }

    onChange(event) {
      if (event.target.matches('[data-cs-sort-form] input[name="sort_by"]')) {
        this.submitThroughNative('sort', event.target.value);
      } else if (event.target.closest('[data-cs-filter-form]')) {
        this.updateGroupDots();
      }
    }

    onSubmit(event) {
      if (event.target.matches('[data-cs-sort-form]')) {
        event.preventDefault();
        const checked = event.target.querySelector('input:checked');
        if (checked) this.submitThroughNative('sort', checked.value);
        return;
      }
      if (!event.target.matches('[data-cs-filter-form]')) return;
      event.preventDefault();
      if (this.validatePrices(event.target)) this.submitThroughNative('filter');
    }

    nativeContext() {
      const form = this.originSection?.querySelector('#FacetFiltersFormMobile')
        || document.getElementById('FacetFiltersFormMobile');
      const controller = form?.closest('facet-filters-form');
      if (!form || typeof controller?.onSubmitHandler !== 'function'
        || typeof controller?.createSearchParams !== 'function') return null;
      return { form, controller };
    }

    // Borrow, never clone, the original markup rendered by snippets/facets.liquid.
    // Explicit form ownership keeps its inputs associated with the native form
    // even while the same DOM nodes are temporarily inside the sticky dialog.
    borrowNativePrices(draftEntries = []) {
      if (this.priceLoans.length) return true;
      const native = this.nativeContext();
      const slots = [...this.querySelectorAll('[data-cs-price-slot]')];
      if (!slots.length) return true;
      if (!native) return false;
      let complete = true;
      slots.forEach((slot) => {
        const price = [...native.form.querySelectorAll('price-range')].find((element) => {
          return element.querySelector('.field__input_min')?.name === slot.dataset.csPriceName;
        });
        const unavailable = slot.querySelector('[data-cs-price-unavailable]');
        if (unavailable) unavailable.hidden = Boolean(price);
        if (!price) { complete = false; return; }
        const inputs = [...price.querySelectorAll('input')].map((input) => ({
          input, value: input.value, form: input.getAttribute('form')
        }));
        const info = price.previousElementSibling?.matches('.mobile-facets__info')
          ? price.previousElementSibling : null;
        const nodes = [info, price].filter(Boolean).map((node) => {
          const home = document.createComment('Coral: original price component return point');
          node.before(home);
          return { node, home };
        });
        this.priceLoans.push({ price, inputs, nodes });
        price.setAttribute('data-cs-price-draft', '');
        inputs.forEach(({ input }) => input.setAttribute('form', native.form.id));
        nodes.forEach(({ node }) => slot.appendChild(node));
        draftEntries.forEach(([name, value]) => {
          const field = inputs.find(({ input }) => input.name === name)?.input;
          if (field) field.value = value;
        });
        price.setMinAndMaxValues?.();
        price.noUifilter?.();
      });
      return complete;
    }

    readPriceDraft() {
      return this.priceLoans.flatMap(({ inputs }) => inputs
        .filter(({ input }) => input.name.startsWith('filter.'))
        .map(({ input }) => [input.name, input.value]));
    }

    releaseNativePrices() {
      this.priceLoans.splice(0).forEach(({ price, inputs, nodes }) => {
        inputs.forEach(({ input, value, form }) => {
          input.value = value;
          input.setCustomValidity('');
          if (form === null) input.removeAttribute('form');
          else input.setAttribute('form', form);
        });
        nodes.forEach(({ node, home }) => {
          // A newer AJAX/Theme Editor response must not be overwritten by stale nodes.
          if (home.isConnected) home.replaceWith(node);
          else { node.remove(); home.remove(); }
        });
        price.removeAttribute('data-cs-price-draft');
        price.setMinAndMaxValues?.();
        price.noUifilter?.();
      });
    }

    submitThroughNative(mode, sortValue, resetSort = false) {
      if (this.pendingCommit || this.nativeRequests.size || this.nativeInputPending) return;
      const native = this.nativeContext();
      if (!native) {
        this.showMessage('The original filter is not ready. Close this panel and use the existing Filter & Sort control.', true);
        return;
      }
      const { form, controller } = native;
      if (mode === 'filter' && this.querySelector('[data-cs-price-unavailable]:not([hidden])')) {
        this.showMessage('The original price filter is unavailable. Use the existing Filter & Sort control.', true);
        return;
      }
      const draft = this.querySelector('[data-cs-filter-form]');
      const priceDraftEntries = mode === 'filter' ? this.readPriceDraft() : [];
      const draftValues = mode === 'filter' && draft ? new FormData(draft) : null;
      priceDraftEntries.forEach(([name, value]) => draftValues?.set(name, value));
      // Return the price nodes BEFORE the native handler serializes/renders its
      // form. Restore applied values first so request rollback has a true snapshot.
      if (mode === 'filter') this.releaseNativePrices();
      const nativeFields = [...form.elements].filter((field) => field.name?.startsWith('filter.'));
      const nativeSort = form.querySelector('select[name="sort_by"]');
      const defaultSort = this.dataset.defaultSort;
      if (resetSort && nativeSort && ![...nativeSort.options].some((option) => option.value === defaultSort)) {
        this.borrowNativePrices(priceDraftEntries);
        this.showMessage('The collection default sort is not available. Close and reopen the filter before clearing.', true);
        return;
      }
      // Snapshot BEFORE resetting sort, so failed requests can restore it.
      const before = [...form.elements].map((field) => ({
        field, value: field.value, checked: field.checked
      }));

      if (mode === 'sort') {
        if (!nativeSort || ![...nativeSort.options].some((option) => option.value === sortValue)) {
          this.showMessage('This sort option is not available in the original filter. Please reopen the panel.', true);
          return;
        }
        nativeSort.value = sortValue;
      } else {
        if (!draft) return;
        const values = draftValues;
        // Do not silently discard an option introduced by a different theme/app.
        for (const [name, value] of values.entries()) {
          if (!name.startsWith('filter.')) continue;
          const supported = nativeFields.some((field) => field.name === name
            && (field.type !== 'checkbox' && field.type !== 'radio' || field.value === value));
          if (!supported) {
            this.borrowNativePrices(priceDraftEntries);
            this.showMessage('The filter options have changed. Close and reopen this panel before applying.', true);
            return;
          }
        }
        nativeFields.forEach((field) => {
          if (field.type === 'checkbox' || field.type === 'radio') {
            field.checked = values.getAll(field.name).includes(field.value);
          } else {
            field.value = values.get(field.name) ?? '';
          }
        });
      }

      // Clear All returns to the collection's configured default sort. Apply
      // and the Sort sheet keep their existing behavior and explicit choices.
      if (resetSort && nativeSort) nativeSort.value = defaultSort;

      // Pass through preview, tracking and other non-facet query context using
      // real hidden form inputs. The existing serializer remains responsible
      // for the submitted query; pagination resets as in the original handler.
      form.querySelectorAll('[data-cs-context-param]').forEach((input) => input.remove());
      const ownedNames = new Set([...form.elements].map((field) => field.name));
      const contextParams = new URLSearchParams(window.location.search);
      contextParams.forEach((value, name) => {
        // Also drop URL-only sorting when the native sorting control is hidden.
        if (resetSort && name === 'sort_by') return;
        if (name.startsWith('filter.') || ['page', 'section_id', 'sections'].includes(name) || ownedNames.has(name)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.dataset.csContextParam = '';
        form.appendChild(input);
      });

      this.pendingCommit = {
        mode, before, priceDraftEntries, previousURL: window.location.href, previousState: history.state,
        key: this.requestKey(controller.createSearchParams(form)), rendered: false
      };
      this.setBusy(true);
      this.showMessage('Updating products…');
      clearTimeout(this.slowTimer);
      this.slowTimer = setTimeout(() => {
        if (this.pendingCommit) this.showMessage('Products are taking longer to update. Please wait, or close this panel.');
      }, 15000);

      // This is the SAME form input event used by the original mobile controls.
      // Its existing debounce -> onSubmitHandler -> onSubmitForm -> renderPage
      // handles the request. No second filtering/AJAX/navigation implementation.
      form.dispatchEvent(new Event('input', { bubbles: true }));
    }

    requestKey(value, isURL = false) {
      const params = isURL
        ? new URL(value, window.location.origin).searchParams
        : new URLSearchParams(value || '');
      params.delete('section_id');
      params.sort();
      return params.toString();
    }

    onNativeInput(event) {
      const form = event.target.closest?.('#FacetFiltersFormMobile');
      if (!form || form !== this.nativeContext()?.form || this.pendingCommit) return;
      // Wait for the original handler's debounce before allowing the other UI
      // to edit stale data. Only the added presentation is temporarily locked.
      this.nativeInputPending = true;
      this.setBusy(true);
      clearTimeout(this.nativeInputTimer);
      this.nativeInputTimer = setTimeout(() => {
        this.nativeInputPending = false;
        this.setBusy(Boolean(this.pendingCommit || this.nativeRequests.size));
      }, 1500);
    }

    onNativeStart(event) {
      // Browser Back/Forward can request a native render while a draft is open.
      if (!this.pendingCommit && this.activeDialog?.dataset.csDialog === 'filter') this.close(false);
      this.nativeInputPending = false;
      clearTimeout(this.nativeInputTimer);
      const key = this.requestKey(event.detail?.searchParams);
      this.nativeRequests.set(key, (this.nativeRequests.get(key) || 0) + 1);
      this.setBusy(true);
    }

    finishNativeRequest(event) {
      const key = this.requestKey(event.detail?.url || window.location.href, true);
      const remaining = (this.nativeRequests.get(key) || 1) - 1;
      if (remaining > 0) this.nativeRequests.set(key, remaining);
      else this.nativeRequests.delete(key);
      return key;
    }

    onNativeComplete(event) {
      const key = this.finishNativeRequest(event);
      if (this.pendingCommit?.key === key) {
        clearTimeout(this.slowTimer);
        this.pendingCommit = null;
        if (this.pendingSource) this.applySource(this.pendingSource);
        this.close();
        this.showMessage('Products updated.');
      }
      this.setBusy(Boolean(this.pendingCommit || this.nativeRequests.size || this.nativeInputPending));
    }

    onNativeError(event) {
      const key = this.finishNativeRequest(event);
      if (this.pendingCommit?.key === key) {
        const failed = this.pendingCommit;
        clearTimeout(this.slowTimer);
        this.pendingCommit = null;
        if (!failed.rendered) {
          // The native handler pushes history before fetching. Restore its form
          // and URL on a failed request, without discarding the shopper's draft.
          failed.before.forEach(({ field, value, checked }) => {
            if (!field.isConnected) return;
            field.value = value;
            if (typeof checked === 'boolean') field.checked = checked;
          });
          if (this.requestKey(window.location.href, true) === key) {
            history.replaceState(failed.previousState, '', failed.previousURL);
            const controllerClass = customElements.get('facet-filters-form');
            if (controllerClass) controllerClass.searchParamsPrev = new URL(failed.previousURL).search.slice(1);
          }
        }
        this.nativeContext()?.form.querySelectorAll('price-range').forEach((price) => {
          price.setMinAndMaxValues?.();
          price.noUifilter?.();
        });
        if (failed.mode === 'filter' && this.activeDialog?.dataset.csDialog === 'filter') {
          this.borrowNativePrices(failed.priceDraftEntries);
        }
        if (failed.mode === 'sort') {
          const current = this.nativeContext()?.form.querySelector('[name="sort_by"]')?.value;
          this.querySelectorAll('[data-cs-sort-form] input').forEach((input) => {
            input.checked = input.value === current;
          });
        }
        this.showMessage('Products could not be updated. Please try again.', true);
      }
      this.setBusy(Boolean(this.pendingCommit || this.nativeRequests.size || this.nativeInputPending));
    }

    setBusy(busy) {
      this.toggleAttribute('data-cs-busy', busy);
      this.querySelectorAll('[data-cs-open="filter"], [data-cs-open="sort"], [data-cs-apply], [data-cs-clear]').forEach((button) => {
        button.disabled = busy;
      });
      this.querySelectorAll('[data-cs-filter-form], [data-cs-sort-form]').forEach((form) => {
        form.setAttribute('aria-busy', String(busy));
        // Do NOT disable the real native form: disabled fields are not serialized.
        // Lock only these presentation controls, restoring sold-out option states.
        form.querySelectorAll('input').forEach((input) => {
          if (busy && !input.hasAttribute('data-cs-was-disabled')) {
            input.dataset.csWasDisabled = String(input.disabled);
            input.disabled = true;
          } else if (!busy && input.hasAttribute('data-cs-was-disabled')) {
            input.disabled = input.dataset.csWasDisabled === 'true';
            delete input.dataset.csWasDisabled;
          }
        });
      });
      this.querySelectorAll('[data-cs-apply]').forEach((button) => {
        button.textContent = busy ? 'Applying…' : 'Apply';
      });
    }

    showMessage(text, error = false) {
      this.querySelectorAll('[data-cs-status]').forEach((element) => {
        element.textContent = text;
        element.classList.toggle('cs-tools__status--error', error);
        element.hidden = !text;
      });
    }

    syncSortSelects(value) {
      if (!value) return;
      // Original theme has separate desktop/mobile selects. Keep their values
      // aligned without dispatching another event or starting duplicate requests.
      document.querySelectorAll('facet-filters-form select[name="sort_by"]').forEach((select) => {
        if ([...select.options].some((option) => option.value === value)) select.value = value;
      });
    }

    open(name, trigger) {
      if (!this.media.matches || this.nativeDrawer?.open) return;
      if (name !== 'category' && (this.pendingCommit || this.nativeRequests.size || this.nativeInputPending)) return;
      this.showMessage('');
      const dialog = this.querySelector(`[data-cs-dialog="${name}"]`);
      if (!dialog) return;
      if (this.activeDialog) this.close(false);
      if (this.pendingSource) this.applySource(this.pendingSource);
      if (name === 'filter') {
        this.querySelector('[data-cs-filter-form]')?.reset();
        if (!this.borrowNativePrices()) {
          this.showMessage('The original price filter is unavailable. Use the existing Filter & Sort control.', true);
        }
        this.updateGroupDots();
      }
      if (name === 'sort') {
        const current = this.nativeContext()?.form.querySelector('[name="sort_by"]')?.value || this.dataset.currentSort || this.dataset.defaultSort;
        this.querySelectorAll('[data-cs-sort-form] input').forEach((input) => { input.checked = input.value === current; });
      }
      this.returnFocus = trigger || document.activeElement;
      this.activeDialog = dialog;
      this.dataset.open = name;
      this.querySelectorAll('[data-cs-open]').forEach((button) => {
        button.setAttribute('aria-expanded', String(button.dataset.csOpen === name));
      });
      this.lockScroll();
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else {
        dialog.dataset.csFallback = '';
        dialog.setAttribute('open', '');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
      }
      dialog.querySelector('.cs-tools__title')?.focus({ preventScroll: true });
      requestAnimationFrame(() => this.priceLoans.forEach(({ price }) => price.noUifilter?.()));
    }

    close(restoreFocus = true) {
      const dialog = this.activeDialog;
      if (!dialog) return;
      this.activeDialog = null;
      this.releaseNativePrices();
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      delete this.dataset.open;
      this.querySelectorAll('[data-cs-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.unlockScroll();
      if (restoreFocus && this.returnFocus?.isConnected && this.media.matches) {
        this.returnFocus.focus({ preventScroll: true });
      }
      this.returnFocus = null;
      if (this.pendingSource && !this.pendingCommit) this.applySource(this.pendingSource);
    }

    lockScroll() {
      if (this.savedScroll) return;
      const body = document.body;
      this.savedScroll = { x: window.scrollX, y: window.scrollY, styles: {} };
      ['position', 'top', 'left', 'width', 'overflow'].forEach((key) => {
        this.savedScroll.styles[key] = [body.style.getPropertyValue(key), body.style.getPropertyPriority(key)];
      });
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `-${this.savedScroll.y}px`);
      body.style.setProperty('left', '0');
      body.style.setProperty('width', '100%');
      body.style.setProperty('overflow', 'hidden');
    }

    unlockScroll() {
      if (!this.savedScroll) return;
      const saved = this.savedScroll;
      this.savedScroll = null;
      Object.entries(saved.styles).forEach(([key, [value, priority]]) => {
        if (value) document.body.style.setProperty(key, value, priority);
        else document.body.style.removeProperty(key);
      });
      const html = document.documentElement;
      const behavior = [html.style.getPropertyValue('scroll-behavior'), html.style.getPropertyPriority('scroll-behavior')];
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(saved.x, saved.y);
      if (behavior[0]) html.style.setProperty('scroll-behavior', ...behavior);
      else html.style.removeProperty('scroll-behavior');
    }

    selectTab(tab, focus = false) {
      const group = tab.closest('[data-cs-tabs]');
      if (!group) return;
      group.querySelectorAll('[data-cs-tab]').forEach((button) => {
        const selected = button === tab;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      const scrollArea = group.querySelector('.cs-tools__filter-values, .cs-tools__category-content');
      if (scrollArea) scrollArea.scrollTop = 0;
      if (focus) tab.focus({ preventScroll: true });
      requestAnimationFrame(() => this.priceLoans.forEach(({ price }) => price.noUifilter?.()));
    }

    clearDraft() {
      // Ignore repeated clicks while the existing facet engine is updating.
      if (this.pendingCommit || this.nativeRequests.size || this.nativeInputPending) return;
      const form = this.querySelector('[data-cs-filter-form]');
      if (!form) return;
      form.querySelectorAll('input').forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
        else if (input.type === 'number') {
          input.value = '';
          input.setCustomValidity('');
        }
      });
      this.priceLoans.forEach(({ price }) => {
        price.setMinAndMaxValues?.();
        price.noUifilter?.();
      });
      this.updateGroupDots();

      // Empty filters plus the collection's actual default sort produce a clean
      // collection URL through facets.js, not a cosmetic history-only rewrite.
      this.submitThroughNative('filter', undefined, true);
    }

    updateGroupDots() {
      this.querySelectorAll('[data-cs-tabs="filters"] [data-cs-tab]').forEach((tab) => {
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        const dot = tab.querySelector('[data-cs-group-dot]');
        if (!panel || !dot) return;
        dot.hidden = ![...panel.querySelectorAll('input')].some((input) => {
          return input.type === 'checkbox' ? input.checked : input.type === 'number' && input.value !== '';
        });
      });
    }

    validatePrices(form) {
      // Validation and min/max synchronization belong to the original PriceRange
      // component. Check its actual inputs, not a second set of price fields.
      const invalid = [...form.querySelectorAll('input')].find((input) => !input.validity.valid);
      if (!invalid) return true;
      const panel = invalid.closest('[role="tabpanel"]');
      const tab = panel && document.getElementById(panel.getAttribute('aria-labelledby'));
      if (tab) this.selectTab(tab);
      invalid.focus();
      invalid.reportValidity();
      return false;
    }

    isOutsideDialog(event, dialog) {
      if (event.target !== dialog) return false;
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    onKeyDown(event) {
      if (!this.activeDialog) return;
      if (event.key === 'Enter' && event.target.matches('[data-cs-price-slot] input[type="number"]')) {
        event.preventDefault();
        this.querySelector('[data-cs-filter-form]')?.requestSubmit();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      const tab = event.target.closest('[data-cs-tab]');
      if (tab && ['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-cs-tab]')];
        let next = tabs.indexOf(tab);
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else next = (next + (event.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length;
        this.selectTab(tabs[next], true);
        return;
      }
      // Native dialogs contain focus; this also supports the non-dialog fallback.
      if (event.key === 'Tab') {
        const candidates = [...this.activeDialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]')]
          .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
        if (!candidates.length) { event.preventDefault(); return; }
        const first = candidates[0];
        const last = candidates[candidates.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !candidates.includes(active))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (active === last || !candidates.includes(active))) {
          event.preventDefault(); first.focus();
        }
      }
    }

    onFacetsRendered(event) {
      const source = event.detail?.document?.getElementById(this.id);
      if (!source) return;
      this.syncSortSelects(source.dataset.currentSort);
      if (this.pendingCommit) this.pendingCommit.rendered = true;
      if (this.activeDialog || this.pendingCommit) this.pendingSource = source;
      else this.applySource(source);
    }

    applySource(source) {
      this.releaseNativePrices();
      this.pendingSource = null;
      ['[data-cs-filter-form]', '[data-cs-sort-form]'].forEach((selector) => {
        const target = this.querySelector(selector);
        const replacement = source.querySelector(selector);
        if (target && replacement) target.innerHTML = replacement.innerHTML;
      });
      this.dataset.currentSort = source.dataset.currentSort || this.dataset.currentSort;
      this.dataset.defaultSort = source.dataset.defaultSort || this.dataset.defaultSort;
      this.dataset.appliedCount = source.dataset.appliedCount || '0';
      const badge = this.querySelector('[data-cs-applied-count]');
      if (badge) {
        badge.textContent = this.dataset.appliedCount;
        badge.hidden = this.dataset.appliedCount === '0';
      }
      this.updateGroupDots();
      this.setBusy(Boolean(this.pendingCommit || this.nativeRequests.size || this.nativeInputPending));
    }
  }

  customElements.define(tagName, CoralMobileCollectionTools);
})();
